import {Public} from './Public.js';

/**
 * RoomKeeper converses with a server to determine the state of a room,
 * whether the stream has started in the room, and whether the user has
 * a ticket for the room.
 * 
 * It can converse with RoomMonitor which will display the room state.
 */
export class RoomKeeper {
	constructor(el) {
		this.el = el;
		this.room = el.getAttribute(`data-room`);
		el.style.display = `none`;
		this.ticket = false;
		
		this.connect = this.connect.bind(this);
		this.connect();

		this.user_detail_sent = false;
		this.user_detail = false;
		document.addEventListener(`veedo-user-name`, evt=>this.userDetail(evt.detail));
		// listenUrl is used for the client to send messages to the RoomKeeper SSE loop
		// It is received as soon as the SSE connects.
		this.listenUrl = false;

		document.addEventListener(`veedo-lib-ready`, evt=>{
			if (this.streamState) {
				document.dispatchEvent(new CustomEvent(`veedo-stream-state`, { detail: this.streamState }));
			}
		});
	}

	// getUUID returns a localStorage UUID specific to the browser. Note that one user with multiple tabs
	// to the view will show as a single UUID, even though they may have multiple tabs open.
	getUUID() {
		let uuid = window.localStorage.getItem(`rk-uuid`);
		if (uuid) {
			return uuid;
		}
		if (self.crypto && `function`==typeof self.crypto.randomUUID) {
			uuid = self.crypto.randomUUID();
		} else {
			// per https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
			uuid = "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
			    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
			  );
		}
		window.localStorage.setItem(`rk-uuid`, uuid);
		return uuid;
	}

	/**
	 * Per https://stackoverflow.com/questions/21831206/eventsource-permanent-auto-reconnection
	 * If SSE disconnects, the browser will try once, in 3s, to reconnect.
	 * If the second reconnect fails, the browser will not retry.
	 * Other folk seem to consider the reconnection quite shaky on a per-browser
	 * basis. We implement our own reconnection by checking for an open connection
	 * or a message from the server. If we've not received one, we force-reconnect.
	 **/
	setReconnectWatcher() {
		if (this.reconnect_timeout) {
			clearTimeout(this.reconnect_timeout);
		}
		this.reconnect_timeout = setTimeout(this.connect, 45000);
	}
	prefix() {
		if (this.el.hasAttribute(`data-prefix`)) {
			return Promise.resolve(this.el.getAttribute(`data-prefix`));
		}
		return Public.getPrefix();
	}
	keeperUrl() {
		return this.prefix().then( p => {
			if (this.el.hasAttribute(`keeper-url`)) {
				p =  this.el.getAttribute(`keeper-url`);
			}
			return p + `events/${encodeURIComponent(this.room)}`;
		});
	}
	postEventsUrl() {
		return this.prefix().then(prefix=>{
			if (this.el.hasAttribute(`keeper-url`)) {
				prefix = this.el.getAttribute(`keeper-url`);
			}
			return prefix + `events/${encodeURIComponent(this.room)}/user`;
		});
	}
	userDetail(detail) {
		if (this.user_detail!=detail) {
			this.user_detail = detail;
			this.user_detail_sent = false;
			this.sendUserDetail();
		}
	}
	sendUserDetail() {
		if (this.ticket && this.user_detail && !this.user_detail_sent) {
			this.postEventsUrl().then( url=>{
				this.user_detail_sent = true;
				fetch(url, {
					method: `POST`,
					body: JSON.stringify(this.user_detail),
					credentials: "include"
				}).catch(err => {
					console.log(err);
					console.log("trying again in 5")
					setTimeout(() => {
						this.sendUserDetail();					
					}, 5000);
				})
			});
			if (this.listenUrl) {
				this.sendListen(this.user_detail);
			}

		}
	}
	connect() {
		// console.log(`RoomKeeper: connect() - 251205`);
		if (this.sse) {
			this.sse.close();
		}
		// We immediately set a reconnect if we're not open within our timeout
		this.setReconnectWatcher();
		this.keeperUrl().then(url => {
			url = url + (url.indexOf('?')>-1 ? "&" : "?") + "uuid=" + encodeURIComponent(this.getUUID());
			console.log(`RoomKeeper: creating EventSource: connecting to ${url}`);
			// Need to send Credentials so that our cookies go with the SSE connection request
			this.sse = new EventSource(url, {withCredentials: true});
			// console.log(`RoomKeeper - EventSource.withCredentials = ${this.sse.withCredentials}`);
			this.sse.addEventListener(`open`, event=>{
				console.log(`RoomKeeper : opened ${url}`);
				this.setReconnectWatcher();
			});
			this.sse.addEventListener(`message`, event=>{
				console.log(`RoomKeeper: /events/${this.room} event: `, event.data);
				this.setReconnectWatcher();
			});
			this.sse.addEventListener(`error`, event=>{				
				console.log(`RoomKeeper: error : `, event);
				// console.error(`NOT RECONNECTING FOR DEBUG PURPOSES`);
				this.connect();
			});
			this.sse.addEventListener(`ping`, event=>{
				console.log(`RoomKeeper: ping : `, event.data);
				this.setReconnectWatcher();
			});
			this.sse.addEventListener(`ticket`, event=> {
				document.cookie = event.data;
				if (this.ticket!=event.data) {
					console.log(`RoomKeeper: ticket : `, event.data);
					this.ticket = event.data;
					this.sendUserDetail();
				}
				this.setReconnectWatcher();
				document.dispatchEvent(new CustomEvent(`veedo-ticket`, { detail: true }));
			});
			this.sse.addEventListener(`proxy`, event=>{
				this.proxy = event.data;
				document.dispatchEvent(new CustomEvent(`veedo-proxy`, { detail: this.proxy }));
				this.setReconnectWatcher();
			});
			this.sse.addEventListener(`no-ticket`, event=>{
				if (this.ticket!=event.data) {
					console.log(`RoomKeeper: no-ticket : `, event.data);
				}
				document.cookie = event.data;
				this.ticket = false;
				this.setReconnectWatcher();
				document.dispatchEvent(new CustomEvent(`veedo-ticket`, { detail: false }));
			});
			this.sse.addEventListener(`stream-state`, event=>{
				console.log(`RoomKeeper: stream-state : `, event.data);
				this.streamState = event.data;
				this.setReconnectWatcher();
				document.dispatchEvent(new CustomEvent(`veedo-stream-state`, { detail: event.data }));
			});
			this.sse.addEventListener(`listen`, evt=>{
				console.log(`RoomKeeper: listen : ` , evt.data);
				let listenUrl = evt.data;
				if (!listenUrl.startsWith(`/`)) {
					listenUrl = `/` + listenUrl;
				}
				this.prefix().then( p=> {
					if (p.endsWith(`/`)) {
						p = p.substr(0, p.length-1);
					}
					this.setListenUrl(p + listenUrl);
				});
			})
			if (1==this.sse.readyState) {
				console.log(`RoomKeeper: readyState == opened to ${url}`);
				this.setReconnectWatcher();
			} else {
				console.log(`RoomKeeper: this.sse.readyState = ${this.sse.readyState}`);
			}
		});
	}
	async setListenUrl(listenUrl) {
		if (listenUrl.startsWith(`/`)) {	// ie no host defined
			const ku = await this.keeperUrl();
			const k = URL.parse(ku);
			var port = ``;
			if (k.port) {
				port = ":" + k.port;
			}
			listenUrl = `${k.protocol}//${k.hostname}${port}${listenUrl}`;
		}
		this.listenUrl = listenUrl;
		console.log(`RoomKeeper: setListenUrl to ${this.listenUrl}`);
		// this.sendListen({"message":`set listen Url ${this.listenUrl}`});
	}
	sendListen(data) {
		return fetch(this.listenUrl, {
			headers: {
				"Content-Type": "application/json"
			},
			method:"POST",
			body: JSON.stringify(data),
			credentials: "include"
		});
	}
	static init() {
		for (const el of document.querySelectorAll(`[data-instance="RoomKeeper"]`)) {
			new RoomKeeper(el);
		}
	}
}

export class RoomKeeperElement extends HTMLElement {
	constructor() {
		super();
		this.keeper = null;
	}
	connectedCallback() {
		if (!this.keeper) {
			// console.log(`calling new RoomKeeper inside RoomKeeperElement`);
			this.keeper = new RoomKeeper(this);
		}
	}
}
customElements.define(`veedo-room-keeper`, RoomKeeperElement);